<template>
  <b-sidebar
    id="add-new-gib-air-rate-sidebar"
    :visible="isActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(val) => $emit('update:is-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1"
      >
        <h5 class="mb-0">
          {{ gibAirRateData.id ? 'Update' : 'Add New' }} Gib Air Rate
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="
            resetForm();
            hide();
          "
        />
      </div>

      <!-- BODY -->
      <validation-observer ref="newGibAirRateForm">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="validateForm()"
        >
          <b-form-group
            label="Pax Fee"
            label-for="pax-fee"
          >
            <validation-provider
              #default="{ errors }"
              name="Pax Fee"
              rules="required"
            >
              <b-form-input
                id="pax-fee"
                v-model="gibAirRateData.paxFee"
                :state="errors.length > 0 ? false : null"
                name="pax-fee"
                type="number"
                step=".01"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="GTA Office Pax Fee"
            label-for="gta-office-fee"
          >
            <validation-provider
              #default="{ errors }"
              name="GTA Office Pax Fee"
              rules="required"
            >
              <b-form-input
                id="gta-office-fee"
                v-model="gibAirRateData.paxFeeGtaoffice"
                :state="errors.length > 0 ? false : null"
                name="gta-office-fee"
                type="number"
                step=".01"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="Taxi Fee"
            label-for="taxi-fee"
          >
            <b-form-input
              id="taxi-fee"
              name="taxi-fee"
              disabled
              v-model="paxFeeTaxi"
            />
          </b-form-group>

          <b-form-group
            label="Baggage Fee"
            label-for="baggage-fee"
          >
            <validation-provider
              #default="{ errors }"
              name="Baggage Fee"
              rules="required"
            >
              <b-form-input
                id="bagagge-fee"
                v-model="gibAirRateData.baggageFeePerBag"
                :state="errors.length > 0 ? false : null"
                name="baggage-fee"
                type="number"
                step=".01"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="GTA Office Baggage Fee"
            label-for="gta-fee-bag"
          >
            <validation-provider
              #default="{ errors }"
              name="GTA Office Baggage Fee"
              rules="required"
            >
              <b-form-input
                id="gta-fee-bag"
                v-model="gibAirRateData.baggageFeePerBagGtaoffice"
                :state="errors.length > 0 ? false : null"
                name="gta-fee-bag"
                type="number"
                step=".01"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group
            label="Taxi Baggage Fee"
            label-for="taxi-fee-bag"
          >
            <b-form-input
              id="taxi-fee-bag"
              name="taxi-fee-bag"
              disabled
              v-model="baggageFeeTaxi"
            />
          </b-form-group>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              variant="primary"
              class="mr-2"
              type="submit"
              :disabled="isLoading"
            >
              <b-spinner
                v-if="isLoading"
                small
                variant="light"
              />
              {{ gibAirRateData.id ? 'Update' : 'Add' }}
            </b-button>
            <b-button
              type="button"
              variant="outline-secondary"
              @click="
                resetForm();
                hide();
              "
            >
              Cancel
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BSidebar,
  BSpinner,
} from 'bootstrap-vue';
import { computed, ref } from '@vue/composition-api';
import { required } from '@validations';
import { useToast } from 'vue-toastification/composition';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import useGibAirRate from '@/modules/rate/composables/useGibAirRate';
import vSelect from 'vue-select';

export default {
  props: {
    isActive: {
      type: Boolean,
      required: true,
    },
    gibAirRate: {
      type: Object,
      required: true,
    },
  },
  setup(props, ctx) {
    const toast = useToast();
    const { createGibAirRate, updateGibAirRate } = useGibAirRate();

    const gibAirRateData = ref(props.gibAirRate);
    const newGibAirRateForm = ref(null);
    const isLoading = ref(false);

    const baggageFeeTaxi = computed(
      () =>
        gibAirRateData.value.baggageFeePerBag -
        gibAirRateData.value.baggageFeePerBagGtaoffice
    );
    const paxFeeTaxi = computed(
      () => gibAirRateData.value.paxFee - gibAirRateData.value.paxFeeGtaoffice
    );

    const resetForm = () => {
      newGibAirRateForm.value.reset();
    };

    const validateForm = async () => {
      if (isLoading.value) return;

      isLoading.value = true;

      const valid = await newGibAirRateForm.value.validate();

      if (!valid) {
        isLoading.value = false;
        return;
      }

      try {
        gibAirRateData.value.id
          ? await updateGibAirRate(gibAirRateData.value)
          : await createGibAirRate(gibAirRateData.value);

        ctx.emit('form-submitted');
      } catch (error) {
        toast.error({
          component: ToastificationContent,
          props: {
            title: 'An error occurred.',
            icon: 'XCircleIcon',
          },
        });
      }

      isLoading.value = false;
    };

    return {
      // Reacitve
      gibAirRateData,
      newGibAirRateForm,
      isLoading,

      // Computed
      baggageFeeTaxi,
      paxFeeTaxi,

      // Method
      resetForm,
      validateForm,

      // Validator
      required,
    };
  },
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BSidebar,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    BSpinner,
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

#add-new-gib-air-rate-sidebar {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
